import type { Decision } from '@shared/types';

/**
 * decisions.md §8b: the AgentBar's assumptions strip shows every OPEN ledger
 * entry except whichever one is already presented as the waiting ask row
 * (matched by `decisionId`, not by content) — so an entry never appears
 * twice at once. Generic over `kind` — no one decision kind gets special
 * treatment here.
 */
export function openAssumptions(decisions: Decision[], waitingDecisionId: string | null): Decision[] {
  return decisions.filter((decision) => decision.status === 'open' && decision.id !== waitingDecisionId);
}

/** Collapsed-strip label: "1 assumption carried" / "3 assumptions carried". */
export function assumptionsStripLabel(count: number): string {
  return `${count} assumption${count === 1 ? '' : 's'} carried`;
}

/** The label the strip shows for a decision's current working assumption. */
export function assumedOptionLabel(decision: Decision): string {
  return decision.options[decision.assumed]?.label ?? '';
}

/**
 * simplify.md §6 / decisions.md §7d: after the assumptions-strip's Change
 * resolves a decision through `/mutations` (never as a chat message —
 * ComposerDock.tsx), the ONE follow-up chat message to send so the
 * orchestrator re-authors what changed — or `null` when the decision
 * `affects` nothing (no re-authoring needed, so no turn is spent). The
 * server matches this to the ledger entry by id (ChatRequest.decisionId,
 * src/agent/decision-routing.ts's `noteForChatDecision`), not by parsing
 * this text, so the message itself stays human-readable.
 */
export function decisionChatFollowUp(decision: Decision, answerIndex: number): string | null {
  if (decision.affects.length === 0) return null;
  const label = decision.options[answerIndex]?.label ?? '';
  return `[decision] ${decision.question} → ${label}`;
}
